/* Phase 5 · HR-Lite — Month grid (locked on 26th) + CSV export */
import React, { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";

export default function MonthGrid() {
  const now = new Date();
  const [year, setYear] = useState(now.getUTCFullYear());
  const [month, setMonth] = useState(now.getUTCMonth() + 1);
  const [data, setData] = useState(null);
  const [showUnlock, setShowUnlock] = useState(false);
  const [unlockReason, setUnlockReason] = useState("");

  const load = async () => {
    const r = await api.get("/hr/attendance/month", { params: { year, month } });
    setData(r.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year, month]);

  const lock = async () => {
    try {
      await api.post("/hr/attendance/lock", { month_key: data.month_key });
      toast.success("Month locked");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const unlock = async () => {
    if (!unlockReason.trim()) return toast.error("Reason required");
    try {
      await api.post("/hr/attendance/unlock", { month_key: data.month_key, reason: unlockReason });
      toast.success("Unlocked");
      setShowUnlock(false); setUnlockReason("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const downloadCSV = async () => {
    // fetch as blob (cookie carries auth)
    const res = await fetch(`${API}/hr/attendance/export?year=${year}&month=${month}`, { credentials: "include" });
    const blob = await res.blob();
    const u = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = u; a.download = `attendance_${data.month_key}.csv`;
    a.click(); URL.revokeObjectURL(u);
  };

  if (!data) return <div className="p-6 text-sm text-[#7C8E9E]">Loading…</div>;

  return (
    <div className="p-4 md:p-6" data-testid="hr-month">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-[#7C8E9E]">HR · MONTH REGISTER</div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#0B2340]">
            {data.month_key}
            {data.locked && (
              <span className="ml-3 px-2 py-0.5 rounded-full text-[10px] uppercase bg-red-100 text-red-700 align-middle">
                Locked
              </span>
            )}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <input type="number" value={year} onChange={e => setYear(parseInt(e.target.value))} className="border rounded px-2 py-1 text-xs w-20" />
          <select value={month} onChange={e => setMonth(parseInt(e.target.value))} className="border rounded px-2 py-1 text-xs">
            {Array.from({ length: 12 }).map((_, i) => <option key={i + 1} value={i + 1}>{i + 1}</option>)}
          </select>
          <button onClick={downloadCSV} data-testid="hr-month-csv"
            className="px-3 py-1.5 rounded-full bg-white border border-slate-200 text-xs">Export CSV</button>
          {!data.locked ? (
            <button onClick={lock} data-testid="hr-month-lock"
              className="px-3 py-1.5 rounded-full bg-red-600 text-white text-xs">Lock month</button>
          ) : (
            <button onClick={() => setShowUnlock(true)} data-testid="hr-month-unlock"
              className="px-3 py-1.5 rounded-full bg-white border border-red-200 text-red-600 text-xs">Unlock…</button>
          )}
        </div>
      </div>

      {data.locked_by_name && (
        <div className="text-xs text-[#7C8E9E] mb-2">Locked by {data.locked_by_name} at {data.locked_at?.slice(0, 16).replace("T", " ")}</div>
      )}

      <div className="bg-white rounded-lg border border-slate-100 overflow-x-auto">
        <table className="min-w-full text-[11px]" data-testid="hr-month-grid">
          <thead className="text-[10px] uppercase tracking-wider text-[#7C8E9E] sticky top-0 bg-white">
            <tr className="border-b border-slate-100">
              <th className="text-left p-2 sticky left-0 bg-white">Name</th>
              {data.dates.map(d => (
                <th key={d} className="p-1 text-center min-w-[24px]">{d.slice(-2)}</th>
              ))}
              <th className="p-2 text-center">P</th><th className="p-2 text-center">A</th>
              <th className="p-2 text-center">H</th><th className="p-2 text-center">L</th>
            </tr>
          </thead>
          <tbody>
            {data.grid.map(row => (
              <tr key={row.staff_id} className="border-b border-slate-50">
                <td className="p-2 sticky left-0 bg-white font-medium">{row.name}
                  <div className="text-[9px] text-[#7C8E9E]">{row.role_title}</div>
                </td>
                {data.dates.map(d => {
                  const v = row.days[d];
                  const bg = v === "P" ? "bg-emerald-50" : v === "A" ? "bg-red-50" : v === "H" ? "bg-amber-50" : v === "L" ? "bg-purple-50" : "";
                  return <td key={d} className={`p-1 text-center ${bg}`}>{v || "·"}</td>;
                })}
                <td className="p-2 text-center font-semibold text-emerald-800">{row.totals.P}</td>
                <td className="p-2 text-center font-semibold text-red-700">{row.totals.A}</td>
                <td className="p-2 text-center font-semibold text-amber-800">{row.totals.H}</td>
                <td className="p-2 text-center font-semibold text-purple-800">{row.totals.L}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showUnlock && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg p-4 max-w-md w-full">
            <h3 className="font-bold mb-2">Unlock {data.month_key}</h3>
            <textarea rows="3" placeholder="Reason (audit-logged)"
              data-testid="hr-unlock-reason"
              className="w-full border rounded px-2 py-1 text-sm mb-3"
              value={unlockReason} onChange={e => setUnlockReason(e.target.value)} />
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowUnlock(false)} className="px-3 py-1 text-xs">Cancel</button>
              <button onClick={unlock} data-testid="hr-unlock-submit"
                className="px-3 py-1 rounded-full bg-red-600 text-white text-xs">Unlock</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
